import React from 'react';

type Variant = 'PROGRESS' | 'COLLECTION' | 'SPENDING';

type Props = {
    title: string;
    /** 0~100 */
    actual: number;
    /** 0~100 (계획/목표) */
    target?: number;
    /** 아래 보조 라벨 */
    subtitle?: string;
    /** 단위 (기본 %) */
    unit?: string;
    /** 게이지 크기 */
    size?: number; // px

    /** ✅ 색상 규칙 적용을 위한 타입 */
    variant: Variant;
};

function clamp(v: number) {
    return Math.max(0, Math.min(100, v));
}

function polar(cx: number, cy: number, r: number, deg: number) {
    const rad = (deg * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx: number, cy: number, r: number, startDeg: number, endDeg: number) {
    const start = polar(cx, cy, r, startDeg);
    const end = polar(cx, cy, r, endDeg);
    const largeArc = endDeg - startDeg <= 180 ? 0 : 1;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

function targetArrowPoints(cx: number, cy: number, r: number, stroke: number, deg: number) {
    // ✅ inner ring 선(스트로크 안쪽 경계)에 tip을 맞춤
    const innerEdgeR = r - stroke / 2;

    // ✅ 중앙(270deg) 근처는 숫자 가릴 수 있으니 샤프트를 자동으로 짧게
    const norm = ((deg % 360) + 360) % 360;
    const distFromTopCenter = Math.abs(norm - 270); // 270이 상단 중앙
    const shaftLen = distFromTopCenter < 12 ? 12 : distFromTopCenter < 20 ? 20 : 26;

    // ✅ 바늘처럼 "센터 방향"으로 길게 뻗되, 너무 안쪽으로는 들어가지 않게 제한
    // (꼬리 더 길게 하고 싶으면 innerEdgeR - 46 값을 더 크게(예: 58~65))
    const minStartR = Math.max(10, innerEdgeR - 46);
    const startR = Math.max(minStartR, innerEdgeR - shaftLen);

    // 방향: start(안쪽) -> tip(바깥쪽, inner ring 선)
    const start = polar(cx, cy, startR, deg);
    const tip = polar(cx, cy, innerEdgeR, deg);

    // 화살촉(삼각형) - tip에서 바깥쪽을 향하도록
    const headLen = 9;
    const headWid = 7;

    // 방향 벡터 (start -> tip)
    const vx = tip.x - start.x;
    const vy = tip.y - start.y;
    const vLen = Math.sqrt(vx * vx + vy * vy) || 1;
    const ux = vx / vLen;
    const uy = vy / vLen;

    // 수직 벡터
    const px = -uy;
    const py = ux;

    // 화살촉 base(팁에서 안쪽으로 headLen 만큼)
    const base = { x: tip.x - ux * headLen, y: tip.y - uy * headLen };
    const left = { x: base.x + px * (headWid / 2), y: base.y + py * (headWid / 2) };
    const right = { x: base.x - px * (headWid / 2), y: base.y - py * (headWid / 2) };

    return { start, base, tip, left, right };
}

/**
 * ✅ 색상 규칙
 * - PROGRESS/COLLECTION: 느리면 red, 빠르면 blue, 계획 이상이면 blue 고정
 * - SPENDING: 느리면 blue, 빠르면 red
 */
function pickTone(variant: Variant, a: number, t?: number) {
    const hasTarget = typeof t === 'number';
    const delta = hasTarget ? a - (t as number) : 0;

    // 기준 색(추천)
    const blue = {
        stroke: 'rgb(37 99 235)', // blue-600 (조금 더 진하게)
        text: 'text-blue-700',
        chip: 'text-blue-800 bg-blue-50 border-blue-200',
    };
    const red = {
        stroke: 'rgb(225 29 72)', // rose-600
        text: 'text-rose-700',
        chip: 'text-rose-800 bg-rose-50 border-rose-200',
    };
    const amber = {
        stroke: 'rgb(245 158 11)', // amber-500 (중간 경고)
        text: 'text-amber-700',
        chip: 'text-amber-800 bg-amber-50 border-amber-200',
    };
    const neutral = {
        stroke: 'rgb(148 163 184)', // slate-400
        text: 'text-slate-700',
        chip: 'text-slate-700 bg-slate-50 border-slate-200',
    };

    if (!hasTarget) {
        return { actualTone: neutral, chipText: '참고', planTone: neutral, delta: 0 };
    }

    // “계획 대비 얼마나 차이나는가”를 3단계로 해석
    // delta >= 0: 계획 이상
    // -10 <= delta < 0: 계획 근접
    // delta < -10: 계획 하회 (또는 비용집행의 경우 반대로 해석)
    const isAhead = delta >= 0;
    const isNear = delta < 0 && delta >= -10;

    // ✅ 계획(수치/화살표)은 기준선 느낌으로 “슬레이트” 추천
    // (원하시면 variant별로 계획도 색을 달리할 수 있지만, 보통 기준선은 중립이 더 깔끔)
    const planTone = neutral;

    // ✅ actual 색상 결정
    if (variant === 'SPENDING') {
        // 비용집행: 느릴수록 blue, 빠를수록 red
        // (집행이 계획 이상으로 빨라지는게 위험)
        if (isAhead) return { actualTone: red, chipText: '집행 빠름', planTone, delta };
        if (isNear) return { actualTone: amber, chipText: '집행 근접', planTone, delta };
        return { actualTone: blue, chipText: '집행 느림', planTone, delta };
    }

    // PROGRESS / COLLECTION
    // 느릴수록 red, 빠를수록 blue, 계획 이상이면 계속 blue
    if (isAhead) return { actualTone: blue, chipText: '계획 이상', planTone, delta };
    if (isNear) return { actualTone: amber, chipText: '계획 근접', planTone, delta };
    return { actualTone: red, chipText: '계획 하회', planTone, delta };
}

/**
 * 반원(속도계 느낌) 게이지:
 * - 실제(actual)는 두꺼운 호로 채움 + 실적 숫자와 색 통일
 * - 목표(target)는 화살표로 표시 + 계획 숫자와 색 통일 (중립 톤 추천)
 */
export default function GaugeRing({ title, actual, target, subtitle, unit = '%', size = 220, variant }: Props) {
    const w = size;
    const h = Math.round(size * 0.72);
    const cx = w / 2;
    const cy = h * 0.88;

    const stroke = 24;
    const pad = stroke / 2;

    // ✅ 링이 커져도 잘리지 않도록 안전 반지름
    const r = Math.min(cx - pad, cy - pad);

    const startDeg = 180 + 18; // 198
    const endDeg = 360 - 18; // 342
    const span = endDeg - startDeg;

    const a = clamp(actual);
    const t = target == null ? undefined : clamp(target);

    const actualEnd = startDeg + (span * a) / 100;
    const targetDeg = t == null ? undefined : startDeg + (span * t) / 100;

    const { actualTone, planTone, chipText } = pickTone(variant, a, t);

    return (
        <div className="min-w-0 rounded-2xl border bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900">{title}</div>
                    {subtitle && <div className="mt-0.5 text-xs text-gray-500">{subtitle}</div>}
                </div>

                <span className={['shrink-0 rounded-full border px-2 py-0.5 text-[11px]', actualTone.chip].join(' ')}>
                    {chipText}
                </span>
            </div>

            <div className="mt-3 flex items-center justify-center">
                <svg
                    width={w}
                    height={h}
                    viewBox={`0 0 ${w} ${h}`}
                    role="img"
                    aria-label={title}
                    className="h-auto w-full max-w-[220px]"
                >
                    {/* track */}
                    <path
                        d={arcPath(cx, cy, r, startDeg, endDeg)}
                        fill="none"
                        stroke="rgb(229 231 235)" // gray-200
                        strokeWidth={stroke}
                        strokeLinecap="round"
                    />

                    {/* actual arc */}
                    <path
                        d={arcPath(cx, cy, r, startDeg, actualEnd)}
                        fill="none"
                        stroke={actualTone.stroke}
                        strokeWidth={stroke}
                        strokeLinecap="round"
                    />

                    {/* target arrow (plan marker) */}
                    {targetDeg != null &&
                        (() => {
                            const A = targetArrowPoints(cx, cy, r, stroke, targetDeg);

                            return (
                                <g opacity={0.92}>
                                    {/* shaft */}
                                    <line
                                        x1={A.start.x}
                                        y1={A.start.y}
                                        x2={A.base.x}
                                        y2={A.base.y}
                                        stroke={planTone.stroke}
                                        strokeWidth={2.8}
                                        strokeLinecap="round"
                                    />
                                    {/* head */}
                                    <path
                                        d={`M ${A.tip.x} ${A.tip.y} L ${A.left.x} ${A.left.y} L ${A.right.x} ${A.right.y} Z`}
                                        fill={planTone.stroke}
                                    />
                                </g>
                            );
                        })()}

                    {/* center value (actual) */}
                    <text
                        x={cx}
                        y={cy - 18}
                        textAnchor="middle"
                        className={actualTone.text}
                        fontSize="28"
                        fontWeight="700"
                    >
                        {a.toFixed(0)}
                        {unit}
                    </text>

                    {/* bottom plan label (target) */}
                    {t != null && (
                        <text x={cx} y={cy + 10} textAnchor="middle" className={planTone.text} fontSize="12">
                            계획 {t.toFixed(0)}
                            {unit}
                        </text>
                    )}
                </svg>
            </div>

            {/* small numeric row */}
            <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
                <span>현재</span>
                <span className={[actualTone.text, 'font-semibold'].join(' ')}>
                    {a.toFixed(1)}
                    {unit}
                </span>
            </div>

            {t != null && (
                <div className="mt-0.5 flex items-center justify-between text-xs text-gray-500">
                    <span>계획</span>
                    <span className={[planTone.text, 'font-semibold'].join(' ')}>
                        {t.toFixed(1)}
                        {unit}
                    </span>
                </div>
            )}
        </div>
    );
}
